/**
 * Copy for the Apple "Hide My Email" notices on the login page — the beta-gate
 * rejections that are *not* failures (rendered as a notice instead of an error
 * toast), plus the pre-emptive note on the signup tab.
 *
 * These live here rather than inline because the same message has to render
 * from three entry points (the web bounce via `AuthCallbackPage` →
 * `LoginPage`'s navigation state, and the native path via
 * `mapNativeAppleError` → `applyNativeAppleOutcome`). Inlining it three times
 * is how the three copies drift apart.
 */

/**
 * Apple "Hide My Email" on the LOGIN path: the relay alias
 * (`…@privaterelay.appleid.com`) matches no existing account, so the backend
 * cannot link the Apple identity to the account the user already has and
 * rejects with `APPLE_HIDDEN_EMAIL` (src/docs/APPLE_HIDDEN_EMAIL_LINKING.md §3
 * scenario 4).
 *
 * **This notice is read by two different people and cannot tell them apart.**
 * The gate branches on nothing but "relay alias + login intent", and a relay
 * address carries zero linkage to a real account (§5), so an existing user
 * hiding their email and a brand-new user hiding their email produce identical
 * input. Hence the conditional wording: an unconditional instruction is right
 * for one of them and wrong for the other, whichever one we pick.
 *
 * Three constraints on this wording:
 *
 *  1. It must never tell an EXISTING user to sign up. That is the bug being
 *     fixed — it is exactly how a user with a Google account ends up with a
 *     second, unrelated Trade Loop account. Pointing a *self-identified* new
 *     user at the Sign Up tab is fine and is not that bug.
 *  2. It must leave a genuinely new user somewhere to go. Signing up with Apple
 *     + Hide My Email is supported (§3 scenario 2) — `applyBetaGate` approves a
 *     relay address on the signup path before the relay branch is reached — it
 *     just has to happen on the Sign Up tab.
 *  3. The Settings clause names a real destination now that card 4 has shipped:
 *     Settings → Connected Accounts, at {@link CONNECTED_ACCOUNTS_PATH}. It is
 *     still plain text rather than a link, because the reader of this notice is
 *     by definition signed out — a link would bounce them to /login and back.
 */
export const APPLE_HIDDEN_EMAIL_NOTICE = {
  title: "We couldn't match your Apple sign-in",
  message:
    "Apple hid your email address, so we couldn't match it to a Trade Loop account. " +
    'If you already have one, sign in the way you normally do — with your email and ' +
    'password, or with Google — then open Settings → Connected Accounts and ' +
    "connect Apple there. If you're new, switch to Sign Up and continue with Apple there.",
} as const;

/**
 * Where the "Connected Accounts" section lives (card 4). Exported so the copy
 * above and any future in-app link point at the same route.
 */
export const CONNECTED_ACCOUNTS_PATH = '/settings#connected-accounts';

/**
 * Apple "Hide My Email" on the SIGNUP path (§3 scenario 5, card 3). A user who
 * already has an account and signs up with Apple + Hide gets a second, unrelated
 * one — and that is **not detectable**: the relay alias carries zero linkage to
 * the real account (§5), so there is no gate to add and nothing to branch on.
 * Telling them before they choose is the only mitigation there is.
 *
 * Deliberately informational rather than a warning: most signups really are new
 * users, for whom Hide My Email is a perfectly good choice (§3 scenario 2), so
 * the note leads with that and mentions the duplicate only as the "if you
 * already have one" branch.
 *
 * Like `APPLE_HIDDEN_EMAIL_NOTICE` above, the Settings clause now names the
 * real destination shipped by card 4 ({@link CONNECTED_ACCOUNTS_PATH}).
 */
export const APPLE_HIDE_MY_EMAIL_SIGNUP_NOTE =
  "Apple's Hide My Email creates a brand-new account — perfect if you're new here. " +
  'Already have a Trade Loop account? Sign in with it instead, then connect Apple ' +
  'from Settings → Connected Accounts.';
